var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/claims/claim-rejected-reasons.schema.ts
import { z } from "zod";
import { BaggageDisruptionTypes, ClaimRejectedReasonEnum, FlightStatusEnum, ProductTypeEnum } from "@repo/lib-koala-types";
import { airlineIataSchema, dateTimeWithUtcOffsetSchema, durationSchema, flightNumberSchema } from "@repo/schema-common";
var claimRequestedTooLateReasonSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.CLAIM_REQUESTED_TOO_LATE),
  context: z.strictObject({
    claimDate: dateTimeWithUtcOffsetSchema,
    coveredEventSignificantDateForClaimDeadline: dateTimeWithUtcOffsetSchema,
    validityPeriod: durationSchema,
    type: z.nativeEnum(ProductTypeEnum)
  })
});
var contractIsNotActiveSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.CONTRACT_HAS_BEEN_RENOUNCED),
  context: z.strictObject({
    renouncedAt: dateTimeWithUtcOffsetSchema
  })
});
var flightNotCanceledSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.FLIGHT_NOT_CANCELED),
  context: z.strictObject({
    airlineIata: airlineIataSchema,
    flightNumber: flightNumberSchema
  })
});
var flightCanceledTooEarlySchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.FLIGHT_CANCELED_TOO_EARLY),
  context: z.strictObject({
    airlineIata: airlineIataSchema,
    flightNumber: flightNumberSchema
  })
});
var flightNotDivertedSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.FLIGHT_NOT_DIVERTED),
  context: z.strictObject({
    airlineIata: airlineIataSchema,
    flightNumber: flightNumberSchema
  })
});
var flightNotDelayedSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.FLIGHT_NOT_DELAYED),
  context: z.strictObject({
    airlineIata: airlineIataSchema,
    flightNumber: flightNumberSchema,
    status: z.nativeEnum(FlightStatusEnum)
  })
});
var flightLandedWithoutDelayInformationSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.FLIGHT_LANDED_WITHOUT_DELAY_INFO),
  context: z.strictObject({
    airlineIata: airlineIataSchema,
    flightNumber: flightNumberSchema
  })
});
var flightNotDelayedEnoughSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.FLIGHT_NOT_DELAYED_ENOUGH),
  context: z.strictObject({
    airlineIata: airlineIataSchema,
    flightNumber: flightNumberSchema,
    minimumDelay: z.number().min(0),
    actualDelay: z.number().nullable()
  })
});
var tripHasCanceledFlightsSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.TRIP_HAS_CANCELED_FLIGHTS),
  context: z.strictObject({
    flightList: z.array(z.strictObject({
      airlineIata: airlineIataSchema,
      flightNumber: flightNumberSchema,
      startDate: dateTimeWithUtcOffsetSchema
    }))
  })
});
var rejectedManuallySchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.REJECTED_MANUALLY),
  context: z.strictObject({
    note: z.string(),
    translatedNote: z.string().optional()
  })
});
var baggageDisruptionNotCoveredSchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.BAGGAGE_DISRUPTIONS_NOT_COVERED),
  context: z.strictObject({
    disruptionsNotCovered: z.array(z.nativeEnum(BaggageDisruptionTypes))
  })
});
var mismatchBetweenDisruptedAndCheckedBaggage = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.MISMATCH_BETWEEN_DISRUPTED_AND_CHECKED_BAGGAGE),
  context: z.strictObject({
    mismatches: z.array(z.strictObject({
      travelerId: z.string().uuid(),
      numberOfCheckedBags: z.number().int().optional(),
      numberOfAffectedBags: z.number().int()
    }))
  })
});
var claimRequestedTooEarlySchema = z.strictObject({
  reason: z.literal(ClaimRejectedReasonEnum.CLAIM_REQUESTED_TOO_EARLY),
  context: z.strictObject({
    claimDate: dateTimeWithUtcOffsetSchema,
    earliestClaimDate: dateTimeWithUtcOffsetSchema,
    type: z.nativeEnum(ProductTypeEnum)
  })
});
var claimRejectedReasonsSchema = z.array(z.discriminatedUnion("reason", [
  claimRequestedTooLateReasonSchema,
  contractIsNotActiveSchema,
  flightNotCanceledSchema,
  flightCanceledTooEarlySchema,
  flightNotDivertedSchema,
  flightNotDelayedSchema,
  flightLandedWithoutDelayInformationSchema,
  flightNotDelayedEnoughSchema,
  tripHasCanceledFlightsSchema,
  rejectedManuallySchema,
  baggageDisruptionNotCoveredSchema,
  mismatchBetweenDisruptedAndCheckedBaggage,
  claimRequestedTooEarlySchema
]));

// src/mappers/claim-rejected-reasons.mapper.ts
import { DateTime, Duration } from "luxon";
import { ClaimRejectedReasonEnum as ClaimRejectedReasonEnum2 } from "@repo/lib-koala-types";
function mapClaimRejectedReasonDtoToClaimRejectedReason(rejectedReason) {
  switch (rejectedReason.reason) {
    case ClaimRejectedReasonEnum2.CONTRACT_HAS_BEEN_RENOUNCED:
      return {
        reason: rejectedReason.reason,
        context: {
          renouncedAt: DateTime.fromISO(rejectedReason.context.renouncedAt, {
            setZone: true
          })
        }
      };
    case ClaimRejectedReasonEnum2.CLAIM_REQUESTED_TOO_LATE:
      return {
        reason: rejectedReason.reason,
        context: {
          type: rejectedReason.context.type,
          claimDate: DateTime.fromISO(rejectedReason.context.claimDate, {
            setZone: true
          }),
          coveredEventSignificantDateForClaimDeadline: DateTime.fromISO(rejectedReason.context.coveredEventSignificantDateForClaimDeadline, {
            setZone: true
          }),
          validityPeriod: Duration.fromISO(rejectedReason.context.validityPeriod)
        }
      };
    case ClaimRejectedReasonEnum2.FLIGHT_NOT_CANCELED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.FLIGHT_CANCELED_TOO_EARLY:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.FLIGHT_NOT_DIVERTED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.FLIGHT_NOT_DELAYED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.FLIGHT_LANDED_WITHOUT_DELAY_INFO:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.FLIGHT_NOT_DELAYED_ENOUGH:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.TRIP_HAS_CANCELED_FLIGHTS:
      return {
        reason: rejectedReason.reason,
        context: {
          flightList: rejectedReason.context.flightList.map((f) => ({
            ...f,
            startDate: DateTime.fromISO(f.startDate, {
              setZone: true
            })
          }))
        }
      };
    case ClaimRejectedReasonEnum2.REJECTED_MANUALLY:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.CLAIM_REQUESTED_TOO_EARLY:
      return {
        reason: rejectedReason.reason,
        context: {
          type: rejectedReason.context.type,
          claimDate: DateTime.fromISO(rejectedReason.context.claimDate, {
            setZone: true
          }),
          earliestClaimDate: DateTime.fromISO(rejectedReason.context.earliestClaimDate, {
            setZone: true
          })
        }
      };
    case ClaimRejectedReasonEnum2.BAGGAGE_DISRUPTIONS_NOT_COVERED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum2.MISMATCH_BETWEEN_DISRUPTED_AND_CHECKED_BAGGAGE:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    default:
      throw new Error(`Unknown claim rejected reason: ${rejectedReason}`);
  }
}
__name(mapClaimRejectedReasonDtoToClaimRejectedReason, "mapClaimRejectedReasonDtoToClaimRejectedReason");
(function(ClaimRejectedReasonsMapper2) {
  function fromClaimRejectedReasonsDto(rejectedReasons) {
    return rejectedReasons.map(mapClaimRejectedReasonDtoToClaimRejectedReason);
  }
  __name(fromClaimRejectedReasonsDto, "fromClaimRejectedReasonsDto");
  ClaimRejectedReasonsMapper2.fromClaimRejectedReasonsDto = fromClaimRejectedReasonsDto;
})(ClaimRejectedReasonsMapper || (ClaimRejectedReasonsMapper = {}));
var ClaimRejectedReasonsMapper;

// src/mappers/claim-rejected-reasons-dto.mapper.ts
import { ClaimRejectedReasonEnum as ClaimRejectedReasonEnum3 } from "@repo/lib-koala-types";
function mapClaimRejectedReasonToClaimRejectedReasonDto(rejectedReason) {
  switch (rejectedReason.reason) {
    case ClaimRejectedReasonEnum3.CONTRACT_HAS_BEEN_RENOUNCED:
      return {
        reason: rejectedReason.reason,
        context: {
          renouncedAt: rejectedReason.context.renouncedAt.toISO()
        }
      };
    case ClaimRejectedReasonEnum3.CLAIM_REQUESTED_TOO_LATE:
      return {
        reason: rejectedReason.reason,
        context: {
          type: rejectedReason.context.type,
          claimDate: rejectedReason.context.claimDate.toISO(),
          coveredEventSignificantDateForClaimDeadline: rejectedReason.context.coveredEventSignificantDateForClaimDeadline.toISO(),
          validityPeriod: rejectedReason.context.validityPeriod.toISO()
        }
      };
    case ClaimRejectedReasonEnum3.FLIGHT_NOT_CANCELED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.FLIGHT_CANCELED_TOO_EARLY:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.FLIGHT_NOT_DIVERTED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.FLIGHT_NOT_DELAYED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.FLIGHT_LANDED_WITHOUT_DELAY_INFO:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.FLIGHT_NOT_DELAYED_ENOUGH:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.TRIP_HAS_CANCELED_FLIGHTS:
      return {
        reason: rejectedReason.reason,
        context: {
          flightList: rejectedReason.context.flightList.map((f) => ({
            ...f,
            startDate: f.startDate.toISO()
          }))
        }
      };
    case ClaimRejectedReasonEnum3.REJECTED_MANUALLY:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.CLAIM_REQUESTED_TOO_EARLY:
      return {
        reason: rejectedReason.reason,
        context: {
          type: rejectedReason.context.type,
          claimDate: rejectedReason.context.claimDate.toISO(),
          earliestClaimDate: rejectedReason.context.earliestClaimDate.toISO()
        }
      };
    case ClaimRejectedReasonEnum3.BAGGAGE_DISRUPTIONS_NOT_COVERED:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    case ClaimRejectedReasonEnum3.MISMATCH_BETWEEN_DISRUPTED_AND_CHECKED_BAGGAGE:
      return {
        reason: rejectedReason.reason,
        context: rejectedReason.context
      };
    default:
      throw new Error("Unexpected rejected reason condition");
  }
}
__name(mapClaimRejectedReasonToClaimRejectedReasonDto, "mapClaimRejectedReasonToClaimRejectedReasonDto");
(function(ClaimRejectedReasonsDtoMapper2) {
  function fromClaimRejectedReasons(rejectedReasons) {
    return rejectedReasons.map(mapClaimRejectedReasonToClaimRejectedReasonDto);
  }
  __name(fromClaimRejectedReasons, "fromClaimRejectedReasons");
  ClaimRejectedReasonsDtoMapper2.fromClaimRejectedReasons = fromClaimRejectedReasons;
})(ClaimRejectedReasonsDtoMapper || (ClaimRejectedReasonsDtoMapper = {}));
var ClaimRejectedReasonsDtoMapper;
export {
  ClaimRejectedReasonsDtoMapper,
  ClaimRejectedReasonsMapper,
  claimRejectedReasonsSchema
};
//# sourceMappingURL=index.js.map